import { getActiveLanguageCode } from 'services/i18nService';
import { GENDER, GENDER_HASH } from 'enum/profile';


type LangGender = Record<GENDER, string>
type MultiLanguageGenders = Record<string, Partial<LangGender>>

export const MULTI_LANGUAGE_GENDERS: MultiLanguageGenders = {
  en: {
    male: 'male',
    female: 'female',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'couple',
    all: 'all',
  },
  fr: {
    male: 'homme',
    female: 'femme',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'couple',
    all: 'all',
  },
  de: {
    male: 'manner',
    female: 'frauen',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'paare',
    all: 'all',
  },
  es: {
    male: 'chicos',
    female: 'chicas',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'parejas',
    all: 'all',
  },
  'es-ES': {
    male: 'chicos',
    female: 'chicas',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'parejas',
    all: 'all',
  },
  it: {
    male: 'ragazzi',
    female: 'donne',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'coppie',
    all: 'all',
  },
  ro: {
    male: 'barbati',
    female: 'femei',
    transgender: 'transexuali',
    shemale: 'transexuali',
    couple: 'cupluri',
    all: 'all',
  },
  nl: {
    male: 'mannen',
    female: 'vrouwen',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'koppels',
    all: 'all',
  },
  sv: {
    male: 'killar',
    female: 'tjejer',
    transgender: 'transpersoner',
    shemale: 'transpersoner',
    couple: 'par',
    all: 'all',
  },
  pt: {
    male: 'homens',
    female: 'mulheres',
    transgender: 'transgender',
    shemale: 'transgender',
    couple: 'casais',
    all: 'all',
  },
};

/**
 * Returns english version of passed gender for eg german 'manner' will return english 'male'.
 * @param gender - gender preference in language which is not english.
 */
export const checkSexBasedOnGenderPreference = (gender: string): string | undefined => {
  // eslint-disable-next-line guard-for-in -- Our own object
  for (const lang in MULTI_LANGUAGE_GENDERS) {
    for (const translatedGender in MULTI_LANGUAGE_GENDERS[lang]) {
      if (MULTI_LANGUAGE_GENDERS[lang][translatedGender as GENDER] === gender) {
        return translatedGender;
      }
    }
  }

  return undefined;
};

/**
 * Checks if gender is in english and returns the english version of it if is not
 */
export const checkGenderPreferenceValue = (genderPreference: string): string => {
  if (
    genderPreference !== GENDER.MALE &&
    genderPreference !== GENDER.FEMALE &&
    genderPreference !== GENDER.TRANSGENDER &&
    genderPreference !== GENDER.COUPLE &&
    genderPreference !== GENDER.ALL
  ) {
    return checkSexBasedOnGenderPreference(genderPreference) || GENDER.FEMALE;
  }

  return genderPreference;
};

/**
 * Returns gender names for passed language code if they are supported, otherwise returns english version
 */
export const getTranslatedGender = (code: string): Partial<LangGender> => {
  const activeLanguageCode = getActiveLanguageCode();
  const applyCode = code || activeLanguageCode;

  if (Object.keys(MULTI_LANGUAGE_GENDERS).includes(applyCode)) {
    return MULTI_LANGUAGE_GENDERS[applyCode];
  }

  return MULTI_LANGUAGE_GENDERS.en;
};

/**
 * Compares and returns translated version of passed gender for url change purpose
 * @param genderPreference - genderPreference in english
 * @param gender - gender in foreign language
 */
export const compareGenderPreferenceLanguage = (genderPreference: string, gender: GENDER): string => {
  const activeLanguageCode = getActiveLanguageCode();

  if (Object.keys(MULTI_LANGUAGE_GENDERS).includes(activeLanguageCode)) {
    const langGender = MULTI_LANGUAGE_GENDERS[activeLanguageCode][genderPreference as GENDER];
    if (langGender === gender) {
      return gender;
    }

    return langGender;
  }

  return MULTI_LANGUAGE_GENDERS.en[genderPreference as GENDER];
};

/**
 * Converts gender string parameter to gender hash
 */
export const getGenderHash = (genderStr: string): GENDER_HASH => {
  switch (genderStr) {
    case GENDER.FEMALE:
      return GENDER_HASH.FEMALE;
    case GENDER.MALE:
      return GENDER_HASH.MALE;
    case GENDER.TRANSGENDER:
    case GENDER.SHEMALE:
      return GENDER_HASH.SHEMALE;
    case GENDER.COUPLE:
      return GENDER_HASH.COUPLE;
    case GENDER.ALL:
      return GENDER_HASH.ALL;
    default:
      return GENDER_HASH.FEMALE;
  }
};

/**
 * Converts gender hash to gender string parameter
 */
export const getGenderStr = (genderHash: GENDER_HASH, convertShemaleToTransgender = false): GENDER => {
  switch (genderHash) {
    case GENDER_HASH.FEMALE:
      return GENDER.FEMALE;
    case GENDER_HASH.MALE:
      return GENDER.MALE;
    case GENDER_HASH.SHEMALE:
      return convertShemaleToTransgender ? GENDER.TRANSGENDER : GENDER.SHEMALE;
    case GENDER_HASH.COUPLE:
      return GENDER.COUPLE;
    case GENDER_HASH.ALL:
      return GENDER.ALL;
    default:
      return GENDER.FEMALE;
  }
};
